import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { useAuth } from '../contexts/AuthContext';
import {
  Plus, ChevronLeft, ChevronRight, Calendar, AlertTriangle,
  GripVertical, MapPin, Tv, ExternalLink, ChevronDown, ChevronUp,
  Copy, CalendarDays, CalendarRange, Move, Target, Repeat, FileSpreadsheet,
  Search, X,
} from 'lucide-react';

import {
  SortableMassnahmeRow, ShiftDialog, ActivityDialog,
  CopyShiftDialog, ACTIVITY_TYPES,
} from './shifts';
import ExportPlanDialog from './shifts/ExportPlanDialog';
import MobileShiftView from './shifts/MobileShiftView';
import { useShiftsData } from './shifts/useShiftsData';
import { useShiftsActions } from './shifts/useShiftsActions';
import { useCopyShift } from './shifts/useCopyShift';
import { ReassignSeriesDialog } from './shifts/ReassignSeriesDialog';
import { feiertageAPI } from '../lib/api';

import {
  format, addDays, startOfWeek, startOfMonth, endOfMonth, getISOWeek,
  eachDayOfInterval, isWeekend, addMonths, subMonths, getISOWeekYear,
} from 'date-fns';
import { de } from 'date-fns/locale';

import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from '@dnd-kit/sortable';

const RENDER_STEP = 80;

export default function Shifts() {
  const { isAdmin, hasPermission } = useAuth();
  // Granulare Permission statt Legacy-Stufe -> Standortleiter (shifts:edit)
  // bekommt automatisch den "Maßnahmen verwalten"-Tab und Bearbeitungs-Buttons.
  const canManage = isAdmin || hasPermission('shifts', 'edit');

  const [reassignState, setReassignState] = useState({ open: false, shift: null });
  const [onlySeriesFilter, setOnlySeriesFilter] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('all');
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [viewMode, setViewMode] = useState('week');
  const [monthDate, setMonthDate] = useState(new Date());
  const [collapsedMassnahmen, setCollapsedMassnahmen] = useState(new Set());
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [massnahmeSearch, setMassnahmeSearch] = useState('');
  const [renderLimit, setRenderLimit] = useState(RENDER_STEP);

  // Scroll-Container: Höhe dynamisch an tatsächliche Position koppeln, damit die
  // horizontale Scrollleiste (Freitag/Samstag/Sonntag) IMMER im Sichtfenster bleibt
  // und nicht erst nach dem Herunterscrollen der Seite erreichbar ist.
  const gridScrollRef = useRef(null);
  const [gridMaxHeight, setGridMaxHeight] = useState(null);

  // --- Data & actions hooks ---
  const data = useShiftsData({ weekStart, selectedLocationId, viewMode, monthDate });

  useEffect(() => {
    const recompute = () => {
      const el = gridScrollRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top;
      const available = Math.round(window.innerHeight - top - 24); // 24px Puffer für die Scrollleiste
      setGridMaxHeight((prev) => {
        const next = Math.max(320, available);
        return prev === next ? prev : next;
      });
    };
    recompute();
    const id = window.setTimeout(recompute, 150); // nach Layout-/Datenwechsel nachjustieren
    window.addEventListener('resize', recompute);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('resize', recompute);
    };
  }, [viewMode, selectedLocationId, massnahmeSearch, data.loading, renderLimit]);
  const actions = useShiftsActions({
    shifts: data.shifts,
    massnahmen: data.massnahmen,
    setMassnahmen: data.setMassnahmen,
    selectedLocationId,
    loadShifts: data.loadShifts,
    loadMassnahmen: data.loadMassnahmen,
    loadBaseData: data.loadBaseData,
  });
  const copy = useCopyShift({ weekStart, onReload: data.loadShifts });

  // Auto-select a concrete location ONCE on first load so the Wochenplan isn't
  // leer startet. Standardmäßig der erste Standort (Hauptstandort). Danach kann
  // der Nutzer frei „Alle Standorte" wählen (gruppiertes Gesamt-Raster) –
  // dieser Effekt darf die Auswahl NICHT erneut überschreiben.
  const didAutoSelectLocation = useRef(false);
  useEffect(() => {
    if (didAutoSelectLocation.current || data.locations.length === 0) return;
    didAutoSelectLocation.current = true;
    if (selectedLocationId === 'all') {
      setSelectedLocationId(data.locations[0].id);
    }
  }, [data.locations, selectedLocationId]);

  // Deep-Link von der Maßnahmen-Seite: /shifts?massnahme=<id> öffnet den
  // Einsatzplan vorgefiltert auf den Standort + Namen dieser Maßnahme.
  const [searchParams, setSearchParams] = useSearchParams();
  const appliedFocusRef = useRef(false);
  useEffect(() => {
    const mid = searchParams.get('massnahme');
    if (!mid || appliedFocusRef.current || !data.massnahmen?.length) return;
    const m = data.massnahmen.find((x) => x.id === mid);
    if (!m) return;
    appliedFocusRef.current = true;
    didAutoSelectLocation.current = true;
    if (m.location_id) setSelectedLocationId(m.location_id);
    setMassnahmeSearch(m.name || '');
    setSearchParams({}, { replace: true });
  }, [data.massnahmen, searchParams, setSearchParams]);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const weekDays = Array.from({ length: 5 }, (_, i) => addDays(weekStart, i));
  const calendarWeek = getISOWeek(weekStart);
  const currentWeekKey = `${getISOWeekYear(weekStart)}-W${String(calendarWeek).padStart(2, '0')}`;
  const monthWeekDays = viewMode === 'month'
    ? eachDayOfInterval({ start: startOfMonth(monthDate), end: endOfMonth(monthDate) }).filter((d) => !isWeekend(d))
    : [];

  // --- Feiertage automatisch laden (standortspezifisch, passend zur Serien-Logik) ---
  const [holidayMap, setHolidayMap] = useState({});
  const holidayRangeStart = viewMode === 'week' ? weekStart : startOfMonth(monthDate);
  const holidayRangeEnd = viewMode === 'week' ? addDays(weekStart, 6) : endOfMonth(monthDate);
  const holidayStartKey = format(holidayRangeStart, 'yyyy-MM-dd');
  const holidayEndKey = format(holidayRangeEnd, 'yyyy-MM-dd');
  const visibleYear = viewMode === 'week' ? weekStart.getFullYear() : monthDate.getFullYear();
  useEffect(() => {
    let cancelled = false;
    const applyMap = (holidayList, closureList) => {
      const map = {};
      (holidayList || []).forEach((h) => { map[h.date] = { ...h, is_closure: false }; });
      // Schließtage firmenweit ergänzen (überschreiben keinen echten Feiertag).
      (closureList || []).forEach((c) => {
        if (c.date >= holidayStartKey && c.date <= holidayEndKey && !map[c.date]) {
          map[c.date] = { ...c, is_closure: true };
        }
      });
      if (!cancelled) setHolidayMap(map);
    };
    const startYear = Number(holidayStartKey.slice(0, 4));
    const endYear = Number(holidayEndKey.slice(0, 4));
    const years = startYear === endYear ? [startYear] : [startYear, endYear];
    const closurePromise = Promise.all(
      years.map((y) => feiertageAPI.getCompanyFreeDays(y).then((r) => r.data || []).catch(() => []))
    ).then((lists) => lists.flat());

    const holidayPromise =
      selectedLocationId && selectedLocationId !== 'all'
        ? feiertageAPI.getByLocation(selectedLocationId, holidayStartKey, holidayEndKey)
            .then((res) => res.data?.holidays || [])
            .catch(() => [])
        : feiertageAPI.getFlat(visibleYear)
            .then((res) => res.data || [])
            .catch(() => []);

    Promise.all([holidayPromise, closurePromise])
      .then(([holidays, closures]) => applyMap(holidays, closures))
      .catch(() => { if (!cancelled) setHolidayMap({}); });
    return () => { cancelled = true; };
  }, [selectedLocationId, holidayStartKey, holidayEndKey, visibleYear]);

  // Filter: Nur Maßnahmen anzeigen, die in der aktuellen Wochenansicht aktiv sind.
  // Regeln:
  //   - scheduled_weeks gesetzt (Include-Liste): Maßnahme muss diese Woche enthalten.
  //   - excluded_weeks enthält aktuelle Woche: Maßnahme wird ausgeblendet.
  //   - Monatsansicht: keine Zeilenfilterung (zeigt die gesamte Planung des Monats).
  const isMassnahmeActiveInWeek = (m, weekKey) => {
    if (Array.isArray(m.scheduled_weeks) && m.scheduled_weeks.length > 0) {
      return m.scheduled_weeks.includes(weekKey);
    }
    if (Array.isArray(m.excluded_weeks) && m.excluded_weeks.includes(weekKey)) {
      return false;
    }
    return true;
  };

  // Maßnahme im sichtbaren Zeitraum? Blendet inaktive UND ausgelaufene /
  // noch-nicht-gestartete Maßnahmen aus (Start-/Enddatum vs. sichtbarer Bereich).
  const massnahmeInRange = (m, startIso, endIso) => {
    if (m.is_active === false) return false;
    if (m.end_date && m.end_date < startIso) return false;      // ausgelaufen
    if (m.start_date && m.start_date > endIso) return false;    // noch nicht gestartet
    return true;
  };

  const weekStartIso = format(weekStart, 'yyyy-MM-dd');
  const weekEndIso = format(addDays(weekStart, 6), 'yyyy-MM-dd');
  const monthStartIso = format(startOfMonth(monthDate), 'yyyy-MM-dd');
  const monthEndIso = format(endOfMonth(monthDate), 'yyyy-MM-dd');

  const visibleMassnahmen = viewMode === 'week'
    ? data.massnahmen.filter((m) => massnahmeInRange(m, weekStartIso, weekEndIso) && isMassnahmeActiveInWeek(m, currentWeekKey))
    : data.massnahmen.filter((m) => massnahmeInRange(m, monthStartIso, monthEndIso));

  // Suche + inkrementelles Rendern: bei Standorten mit vielen Maßnahmen werden
  // nicht alle Zeilen auf einmal in den DOM gerendert (Performance).
  const filteredMassnahmen = React.useMemo(() => {
    const q = massnahmeSearch.trim().toLowerCase();
    let list = q
      ? visibleMassnahmen.filter((m) =>
          (m.name || '').toLowerCase().includes(q) ||
          (m.short_code || '').toLowerCase().includes(q))
      : visibleMassnahmen;
    // „Alle Standorte": nach Standort gruppieren (zusammenhängende Blöcke),
    // damit das Raster pro Standort einen Abschnitt zeigt.
    if (selectedLocationId === 'all') {
      list = [...list].sort((a, b) =>
        (a.location_name || 'zzz').localeCompare(b.location_name || 'zzz') ||
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        (a.name || '').localeCompare(b.name || ''));
    }
    return list;
  }, [visibleMassnahmen, massnahmeSearch, selectedLocationId]);

  const renderedMassnahmen = filteredMassnahmen.slice(0, renderLimit);

  useEffect(() => {
    setRenderLimit(RENDER_STEP);
  }, [massnahmeSearch, selectedLocationId, currentWeekKey, viewMode]);

  // --- Abwesenheits-Lookup für Einsatz-Markierung ---
  // Pro Einsatz wird geprüft, ob der zugewiesene Mitarbeiter an diesem Tag
  // Urlaub (genehmigt/beantragt), krank oder anders abwesend ist. Ergebnis:
  // ein Objekt mit Status + Tooltip-Text — oder null. Die Berechnung
  // vermeidet teure Schleifen pro Render, indem Einträge nach employee_id
  // gebucketed werden.
  const absenceLookup = React.useMemo(() => {
    const byEmp = new Map();
    const push = (empId, item) => {
      if (!byEmp.has(empId)) byEmp.set(empId, []);
      byEmp.get(empId).push(item);
    };
    (data.vacations || []).forEach((v) => {
      if (v.status === 'rejected' || v.status === 'cancelled') return;
      push(v.employee_id, {
        kind: 'vacation',
        status: v.status, // 'approved' | 'pending'
        start: v.start_date,
        end: v.end_date,
        label: v.vacation_type || 'Urlaub',
      });
    });
    (data.absences || []).forEach((a) => {
      push(a.employee_id, {
        kind: 'absence',
        status: 'approved',
        start: a.start_date,
        end: a.end_date,
        type: a.absence_type,
        label: a.absence_type_label || a.absence_type || 'Abwesend',
      });
    });
    return byEmp;
  }, [data.vacations, data.absences]);

  const getAbsenceForShift = (employeeId, dayKey) => {
    const list = absenceLookup.get(employeeId);
    if (!list || !list.length) return null;
    // Wir priorisieren genehmigte Abwesenheiten/Urlaube über beantragte.
    const hits = list.filter((e) => dayKey >= e.start && dayKey <= e.end);
    if (!hits.length) return null;
    const approved = hits.find((h) => h.status === 'approved');
    const chosen = approved || hits[0];
    const fmt = (d) => {
      const [y, m, dd] = d.split('-');
      return `${dd}.${m}.${y}`;
    };
    const range = chosen.start === chosen.end ? fmt(chosen.start) : `${fmt(chosen.start)} – ${fmt(chosen.end)}`;
    let tooltip;
    if (chosen.kind === 'vacation') {
      tooltip = chosen.status === 'approved'
        ? `${chosen.label} (genehmigt): ${range}`
        : `${chosen.label} (beantragt – noch offen): ${range}`;
    } else {
      tooltip = `${chosen.label}: ${range}`;
    }
    return { kind: chosen.kind, status: chosen.status, tooltip };
  };

  // Navigation
  const handlePrevWeek = () => setWeekStart(addDays(weekStart, -7));
  const handleNextWeek = () => setWeekStart(addDays(weekStart, 7));
  const handlePrevMonth = () => setMonthDate(subMonths(monthDate, 1));
  const handleNextMonth = () => setMonthDate(addMonths(monthDate, 1));
  const handleToday = () => {
    if (viewMode === 'month') setMonthDate(new Date());
    setWeekStart(startOfWeek(new Date(), { weekStartsOn: 1 }));
  };

  const toggleMassnahmeCollapse = (massnahmeId) => {
    setCollapsedMassnahmen((prev) => {
      const s = new Set(prev);
      if (s.has(massnahmeId)) s.delete(massnahmeId); else s.add(massnahmeId);
      return s;
    });
  };
  const collapseAll = () => setCollapsedMassnahmen(new Set(data.massnahmen.map((m) => m.id)));
  const expandAll = () => setCollapsedMassnahmen(new Set());

  // Ein-/ausklappbare Standort-Gruppen in der „Alle Standorte"-Ansicht.
  const [collapsedLocations, setCollapsedLocations] = useState(new Set());
  const toggleLocationCollapse = (locId) => {
    setCollapsedLocations((prev) => {
      const s = new Set(prev);
      if (s.has(locId)) s.delete(locId); else s.add(locId);
      return s;
    });
  };
  const locationCountMap = React.useMemo(() => {
    const m = {};
    filteredMassnahmen.forEach((x) => {
      const k = x.location_id || '__none__';
      m[k] = (m[k] || 0) + 1;
    });
    return m;
  }, [filteredMassnahmen]);

  const getShiftsForMassnahmeAndDay = (massnahmeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return data.shifts.filter((s) => {
      if (s.massnahme_id !== massnahmeId || s.date !== dateStr) return false;
      if (onlySeriesFilter && !s.series_id) return false;
      return true;
    });
  };
  const getActivitiesForMassnahmeAndDay = (massnahmeId, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return data.activities.filter((a) => a.massnahme_id === massnahmeId && a.date === dateStr);
  };
  const getEmployeeConflicts = (shift, date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Akteur eindeutig bestimmen: Honorarkräfte haben employee_id=null und werden
    // über honorarkraft_id identifiziert. Sonst würden verschiedene Honorarkräfte
    // am selben Tag (alle employee_id=null) fälschlich als Doppelbuchung markiert.
    const actorId = shift.honorarkraft_id || shift.employee_id;
    if (!actorId) return [];
    return data.shifts.filter(
      (s) => (s.honorarkraft_id || s.employee_id) === actorId && s.date === dateStr
    );
  };

  return (
    <DashboardLayout wide>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-heading font-bold">Einsatzplanung</h1>
            <p className="text-muted-foreground">Mitarbeiter in Maßnahmen einteilen</p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Planung */}
          <div className="space-y-4">
            {/* Location & Navigation */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col gap-4">
                  {/* Location */}
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">Standort:</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {canManage && (
                        <Button
                          variant={selectedLocationId === 'all' ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedLocationId('all')}
                        >Alle Standorte</Button>
                      )}
                      {data.locations.map((loc) => (
                        <Button
                          key={loc.id}
                          variant={selectedLocationId === loc.id ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setSelectedLocationId(loc.id)}
                        >{loc.name}</Button>
                      ))}
                    </div>
                    {selectedLocationId && selectedLocationId !== 'all' && (
                      <div className="flex items-center gap-2 ml-auto">
                        {data.locations.find((l) => l.id === selectedLocationId)?.short_id && (
                          <Badge variant="outline" className="font-mono text-xs">
                            ID: {data.locations.find((l) => l.id === selectedLocationId)?.short_id}
                          </Badge>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            const loc = data.locations.find((l) => l.id === selectedLocationId);
                            const urlId = loc?.short_id || selectedLocationId;
                            window.open(`/einsatzplan/tv/${urlId}`, '_blank');
                          }}
                        >
                          <Tv className="h-4 w-4 mr-2" />TV-Ansicht
                          <ExternalLink className="h-3 w-3 ml-1" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setExportDialogOpen(true)}
                          data-testid="open-export-plan-btn"
                        >
                          <FileSpreadsheet className="h-4 w-4 mr-2" />Excel-Export
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Week/Month Navigation */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t pt-4">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex rounded-lg border overflow-hidden mr-2">
                        <Button variant={viewMode === 'week' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setViewMode('week')}>
                          <CalendarDays className="h-4 w-4 mr-1" />Woche
                        </Button>
                        <Button variant={viewMode === 'month' ? 'default' : 'ghost'} size="sm" className="rounded-none" onClick={() => setViewMode('month')}>
                          <CalendarRange className="h-4 w-4 mr-1" />Monat
                        </Button>
                      </div>

                      <Button variant="outline" size="icon" onClick={viewMode === 'week' ? handlePrevWeek : handlePrevMonth}>
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" onClick={handleToday}>Heute</Button>
                      <Button variant="outline" size="icon" onClick={viewMode === 'week' ? handleNextWeek : handleNextMonth}>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                      <div className="ml-2 flex items-center gap-3">
                        {viewMode === 'week' ? (
                          <>
                            <Badge variant="secondary" className="font-mono text-sm">KW {calendarWeek}</Badge>
                            <span className="text-sm font-medium">
                              {format(weekStart, 'dd.MM.', { locale: de })} - {format(addDays(weekStart, 4), 'dd.MM.yyyy', { locale: de })}
                            </span>
                          </>
                        ) : (
                          <span className="text-sm font-medium">{format(monthDate, 'MMMM yyyy', { locale: de })}</span>
                        )}
                      </div>
                      {data.massnahmen.length > 0 && (
                        <div className="flex items-center gap-1 ml-4">
                          <Button variant="ghost" size="sm" onClick={expandAll} title="Alle aufklappen">
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={collapseAll} title="Alle zuklappen">
                            <ChevronUp className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      {selectedLocationId !== 'all' && (data.massnahmen.length > 8 || massnahmeSearch) && (
                        <div className="relative ml-2">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            value={massnahmeSearch}
                            onChange={(e) => setMassnahmeSearch(e.target.value)}
                            placeholder="Maßnahme suchen…"
                            className="h-9 w-48 pl-8 pr-8"
                            data-testid="massnahme-search-input"
                          />
                          {massnahmeSearch && (
                            <button
                              type="button"
                              onClick={() => setMassnahmeSearch('')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                              data-testid="massnahme-search-clear"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {canManage && (
                      <div className="flex flex-wrap items-center gap-2">
                        <Button
                          variant={onlySeriesFilter ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => setOnlySeriesFilter((v) => !v)}
                          data-testid="filter-only-series-btn"
                          title={onlySeriesFilter ? 'Filter deaktivieren' : 'Nur Serien-Einsätze anzeigen'}
                        >
                          <Repeat className="h-4 w-4 mr-2" />
                          {onlySeriesFilter ? 'Nur Serien' : 'Serien filtern'}
                        </Button>
                        <Button variant="outline" onClick={() => actions.openActivityDialog(new Date(), '')}>
                          <Calendar className="h-4 w-4 mr-2" />Neue Aktivität
                        </Button>
                        <Button onClick={() => actions.openNewShiftDialog(new Date())} className="rounded-full">
                          <Plus className="h-4 w-4 mr-2" />Neuer Einsatz
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Grid */}
            {data.massnahmen.length === 0 ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Target className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p className="font-medium">{selectedLocationId === 'all' ? 'Keine Maßnahmen vorhanden' : 'Keine Maßnahmen für diesen Standort'}</p>
                  <p className="text-sm">
                    {canManage
                      ? `Öffnen Sie den Menüpunkt "Maßnahmen", um Ihre erste Maßnahme${selectedLocationId === 'all' ? '' : ` für ${data.locations.find((l) => l.id === selectedLocationId)?.name || 'diesen Standort'}`} anzulegen.`
                      : 'Derzeit sind keine Maßnahmen hinterlegt. Bitte wenden Sie sich an Ihre Teamleitung.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-0">
                  {data.loading ? (
                    <div className="p-6 space-y-4">
                      {[1, 2, 3].map((i) => <Skeleton key={`skel-${i}`} className="h-20 w-full" />)}
                    </div>
                  ) : (
                    <>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={actions.handleDragEnd}>
                      <div
                        ref={gridScrollRef}
                        className="overflow-auto relative hidden lg:block"
                        style={{ maxHeight: gridMaxHeight ? `${gridMaxHeight}px` : 'calc(100vh - 260px)' }}
                      >
                        <table className={`w-full min-w-[760px] ${viewMode === 'week' ? 'table-fixed' : ''}`}>
                          <thead className="sticky top-0 z-20">
                            <tr className="border-b bg-muted/30">
                              <th className="p-3 text-left font-medium w-56 sticky left-0 bg-muted/30 z-30 shadow-[inset_0_-1px_0_0_hsl(var(--border))]">Maßnahme</th>
                              {(viewMode === 'week' ? weekDays : monthWeekDays).map((day) => {
                                const dayKey = format(day, 'yyyy-MM-dd');
                                const holiday = holidayMap[dayKey];
                                return (
                                  <th
                                    key={day.toISOString()}
                                    className={`p-3 text-center font-medium shadow-[inset_0_-1px_0_0_hsl(var(--border))] ${viewMode === 'month' ? 'min-w-[80px]' : 'min-w-[130px]'} ${holiday ? 'bg-red-50 dark:bg-red-950/20' : 'bg-muted/30'}`}
                                    title={holiday ? `${holiday.is_closure ? 'Schließtag' : 'Feiertag'}: ${holiday.name}` : undefined}
                                  >
                                    <div className="text-xs text-muted-foreground">
                                      {format(day, viewMode === 'month' ? 'EEE' : 'EEEE', { locale: de })}
                                    </div>
                                    <div className={dayKey === format(new Date(), 'yyyy-MM-dd') ? 'text-primary font-bold' : ''}>
                                      {format(day, viewMode === 'month' ? 'd.' : 'dd.MM.', { locale: de })}
                                    </div>
                                    {holiday && (
                                      <div className="text-[10px] text-red-700 dark:text-red-400 font-medium truncate mt-0.5" title={holiday.name}>
                                        {holiday.name}
                                      </div>
                                    )}
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            <SortableContext items={renderedMassnahmen.map((m) => m.id)} strategy={verticalListSortingStrategy}>
                              {filteredMassnahmen.length === 0 && viewMode === 'week' ? (
                                <tr>
                                  <td colSpan={weekDays.length + 1} className="p-8 text-center text-sm text-muted-foreground">
                                    {massnahmeSearch.trim()
                                      ? `Keine Maßnahme passt zur Suche „${massnahmeSearch}".`
                                      : `Keine Maßnahmen in KW ${calendarWeek}. Wechseln Sie zu einer anderen Woche oder zur Monatsansicht.`}
                                  </td>
                                </tr>
                              ) : (
                                renderedMassnahmen.map((massnahme, idx) => {
                                  const showLocHeader = selectedLocationId === 'all' &&
                                    (idx === 0 || renderedMassnahmen[idx - 1].location_id !== massnahme.location_id);
                                  const locKey = massnahme.location_id || '__none__';
                                  const locCollapsed = selectedLocationId === 'all' && collapsedLocations.has(locKey);
                                  return (
                                  <React.Fragment key={massnahme.id}>
                                  {showLocHeader && (
                                    <tr
                                      className="bg-secondary/60 border-y cursor-pointer hover:bg-secondary/80 transition-colors"
                                      onClick={() => toggleLocationCollapse(locKey)}
                                      data-testid={`location-group-header-${locKey}`}
                                    >
                                      <td colSpan={(viewMode === 'week' ? weekDays : monthWeekDays).length + 1} className="p-0">
                                        <div className="sticky left-0 inline-flex items-center gap-2 px-3 py-2 font-semibold text-sm">
                                          {locCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                          <MapPin className="h-4 w-4 text-primary" />
                                          {massnahme.location_name || 'Ohne Standort'}
                                          <span className="text-xs text-muted-foreground font-normal">
                                            ({locationCountMap[locKey] || 0} Maßnahme{(locationCountMap[locKey] || 0) === 1 ? '' : 'n'})
                                          </span>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                  {!locCollapsed && (
                                  <SortableMassnahmeRow
                                    massnahme={massnahme}
                                    weekDays={viewMode === 'week' ? weekDays : monthWeekDays}
                                    getShiftsForMassnahmeAndDay={getShiftsForMassnahmeAndDay}
                                    getActivitiesForMassnahmeAndDay={getActivitiesForMassnahmeAndDay}
                                    getEmployeeConflicts={getEmployeeConflicts}
                                    getAbsenceForShift={getAbsenceForShift}
                                    canManage={canManage}
                                    openNewShiftDialog={actions.openNewShiftDialog}
                                    openEditShiftDialog={actions.openEditShiftDialog}
                                    openActivityDialog={actions.openActivityDialog}
                                    openEditActivityDialog={actions.openEditActivityDialog}
                                    handleDeleteActivity={actions.handleDeleteActivity}
                                    ACTIVITY_TYPES={ACTIVITY_TYPES}
                                    isCollapsed={collapsedMassnahmen.has(massnahme.id)}
                                    onToggleCollapse={toggleMassnahmeCollapse}
                                    onCopyShift={copy.openCopyDialog}
                                    onShiftDrop={actions.handleShiftDrop}
                                    onActivityDrop={actions.handleActivityDrop}
                                    onReorderEntry={actions.reorderEntryInCell}
                                    onReorderBeforeTarget={actions.reorderEntryBeforeTarget}
                                    holidayMap={holidayMap}
                                  />
                                  )}
                                  </React.Fragment>
                                  );
                                })
                              )}
                              {filteredMassnahmen.length > renderLimit && (
                                <tr>
                                  <td colSpan={(viewMode === 'week' ? weekDays : monthWeekDays).length + 1} className="p-3 text-center">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => setRenderLimit((n) => n + RENDER_STEP)}
                                      data-testid="massnahmen-load-more-btn"
                                    >
                                      Weitere {Math.min(RENDER_STEP, filteredMassnahmen.length - renderLimit)} von {filteredMassnahmen.length} Maßnahmen anzeigen
                                    </Button>
                                  </td>
                                </tr>
                              )}
                            </SortableContext>
                          </tbody>
                        </table>
                      </div>
                    </DndContext>
                    <div className="lg:hidden">
                      <MobileShiftView
                        weekDays={viewMode === 'week' ? weekDays : monthWeekDays}
                        filteredMassnahmen={filteredMassnahmen}
                        getShiftsForMassnahmeAndDay={getShiftsForMassnahmeAndDay}
                        getActivitiesForMassnahmeAndDay={getActivitiesForMassnahmeAndDay}
                        getEmployeeConflicts={getEmployeeConflicts}
                        getAbsenceForShift={getAbsenceForShift}
                        canManage={canManage}
                        openNewShiftDialog={actions.openNewShiftDialog}
                        openEditShiftDialog={actions.openEditShiftDialog}
                        openActivityDialog={actions.openActivityDialog}
                        openEditActivityDialog={actions.openEditActivityDialog}
                        handleDeleteActivity={actions.handleDeleteActivity}
                        holidayMap={holidayMap}
                      />
                    </div>
                    </>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Legende */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                <span>Mitarbeiter ist am selben Tag in mehreren Maßnahmen eingeteilt</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded ring-2 ring-red-500" />
                <span>Mitarbeiter ist abwesend (Urlaub/Krank, genehmigt)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="inline-block w-4 h-4 rounded outline outline-2 outline-offset-1 outline-dashed outline-red-400" />
                <span>Urlaub beantragt (noch offen)</span>
              </div>
              {canManage && (
                <>
                  <div className="flex items-center gap-2"><GripVertical className="h-4 w-4" /><span>Maßnahmen per Drag & Drop sortieren</span></div>
                  <div className="flex items-center gap-2"><Move className="h-4 w-4" /><span>Einsätze zwischen Tagen <span className="font-medium">und Maßnahmen</span> verschieben</span></div>
                  <div className="flex items-center gap-2"><Copy className="h-4 w-4" /><span>Einsatz auf mehrere Tage kopieren</span></div>
                </>
              )}
            </div>
          </div>
        </div>

        <ShiftDialog
          open={actions.dialogOpen}
          onOpenChange={actions.setDialogOpen}
          selectedShift={actions.selectedShift}
          formData={actions.formData}
          setFormData={actions.setFormData}
          massnahmen={data.massnahmen}
          employees={data.employees}
          locations={data.locations}
          rooms={data.rooms}
          saving={actions.saving}
          onSave={actions.handleSaveShift}
          onDelete={() => { actions.handleDeleteShift(actions.selectedShift.id); actions.setDialogOpen(false); }}
          onDeleteSeries={actions.handleDeleteSeries}
          onReassignSeries={(shift) => setReassignState({ open: true, shift })}
          selectedLocationId={selectedLocationId}
          recurrence={actions.recurrence}
          setRecurrence={actions.setRecurrence}
          multiDayDays={actions.multiDayDays}
          setMultiDayDays={actions.setMultiDayDays}
        />

        <ExportPlanDialog
          open={exportDialogOpen}
          onOpenChange={setExportDialogOpen}
          locationId={selectedLocationId}
          locationName={data.locations.find((l) => l.id === selectedLocationId)?.name}
        />

        <ActivityDialog
          open={actions.activityDialogOpen}
          onOpenChange={actions.setActivityDialogOpen}
          formData={actions.activityForm}
          setFormData={actions.setActivityForm}
          massnahmen={data.massnahmen}
          saving={actions.saving}
          onSave={actions.handleSaveActivity}
          recurrence={actions.activityRecurrence}
          setRecurrence={actions.setActivityRecurrence}
          multiDayDays={actions.activityMultiDayDays}
          setMultiDayDays={actions.setActivityMultiDayDays}
          selectedActivity={actions.selectedActivity}
        />

        <CopyShiftDialog
          open={copy.copyDialogOpen}
          onOpenChange={copy.setCopyDialogOpen}
          shiftToCopy={copy.shiftToCopy}
          entityType={copy.entityType}
          massnahmen={data.massnahmen}
          targetMassnahmeId={copy.targetMassnahmeId}
          setTargetMassnahmeId={copy.setTargetMassnahmeId}
          deleteSourceAfterCopy={copy.deleteSourceAfterCopy}
          setDeleteSourceAfterCopy={copy.setDeleteSourceAfterCopy}
          copyDays={copy.copyDays}
          copyWeekStart={copy.copyWeekStart}
          copyDateRange={copy.copyDateRange}
          setCopyDateRange={copy.setCopyDateRange}
          saving={copy.saving}
          selectWeekForCopy={copy.selectWeekForCopy}
          selectNextWeek={copy.selectNextWeek}
          selectNextTwoWeeks={copy.selectNextTwoWeeks}
          selectUntilMonthEnd={copy.selectUntilMonthEnd}
          clearCopyDays={copy.clearCopyDays}
          selectFromDateRange={copy.selectFromDateRange}
          getWeekdaysInRange={copy.getWeekdaysInRange}
          handleCopyPrevWeek={copy.handleCopyPrevWeek}
          handleCopyNextWeek={copy.handleCopyNextWeek}
          getCopyWeekDays={copy.getCopyWeekDays}
          toggleCopyDay={copy.toggleCopyDay}
          handleCopyShift={copy.handleCopyShift}
        />

        <ReassignSeriesDialog
          open={reassignState.open}
          onOpenChange={(o) => setReassignState({ open: o, shift: o ? reassignState.shift : null })}
          seriesId={reassignState.shift?.series_id}
          currentEmployeeId={reassignState.shift?.employee_id}
          employees={data.employees}
          onDone={() => {
            actions.setDialogOpen(false);
            data.loadShifts?.();
          }}
        />
      </div>
    </DashboardLayout>
  );
}
